import {apiRefusalMessageOf} from "../account/accountApiFailure";
import {
	isAboveLargestPlanRefusal, isActiveStorageRefusal, isFreeMaxUploadRefusal, isInstantPlanRequiredRefusal,
	isTooManyFilesRefusal,
	personalizationRefusalOf,
} from "../api/apiErrorIds";
import {EncryptedUploadTooLargeError, UploadInterruptedError} from "../core/upload/uploadFailures";
import {
	classifyRenderedFailure,
	reportRenderedFailure,
	RenderedFailureKey,
	type SafeFailureDiagnostic,
} from "../utils/renderedFailure";
import type {OverLimitPricing} from "./overLimit/overLimitOffer";

/** The sender action whose rejection is being presented. */
export enum SenderFailureContext {
	Upload = "UPLOAD",
	Delivery = "DELIVERY",
	Email = "EMAIL",
	Authentication = "AUTHENTICATION",
}

export type SenderFailureKey =
	| "uploadFailed"
	| "deliveryFailed"
	| "emailFailed"
	| "authenticationFailed"
	| "fileTooLarge"
	| "network"
	| "activeStorageFull"
	// More files than a transfer may hold (kitecorp/transferify-ui#2944). Named because the remedy is a
	// smaller selection, which the catalog states with the limit; "try again" would repeat the refusal.
	| "tooManyFiles"
	// A hosted transfer above the largest plan's cap (kitecorp/transferify-api-service#3005). The remedy is a
	// smaller selection, which the catalog states with the limit.
	| "aboveLargestPlan"
	// A FREE Instant send over the free size, which only a plan can take (kitecorp/transferify-api-service#3005, Q5).
	// The composer opens the plan dialog for it; the catalog sentence is the fallback wherever a refusal is shown.
	| "instantPlanRequired"
	// A FREE hosted upload over the free cap, which only a plan can take (kitecorp/transferify-ui#3800). The composer
	// reopens the sized plan dialog for it; the catalog sentence is the fallback wherever a refusal is shown.
	| "hostedPlanRequired"
	// The four per-transfer personalization refusals (api#1099). Named rather than folded into
	// `uploadFailed`, because each has a different remedy and three of them cannot be retried at all.
	| "personalizationPaymentRequired"
	| "personalizationImagesWithoutTemplate"
	| "personalizationAlreadySet"
	| "personalizationNotTheSenders";

export interface SenderFailure {
	messageKey: SenderFailureKey;
	retryAfterSeconds: number | null;
	/**
	 * The api's own sentence for a refusal during the send, shown in place of {@link messageKey}'s copy,
	 * or null (kitecorp/transferify-ui#3010). See {@link apiRefusalMessageOf} for which answers carry one,
	 * and {@link classifySenderFailure} for which context.
	 *
	 * Never part of {@link diagnostic}: that is what gets logged, and the sentence can name the sender's
	 * own data.
	 */
	apiMessage: string | null;
	diagnostic: SafeFailureDiagnostic;
}

const KEY_BY_CONTEXT: Record<SenderFailureContext, SenderFailureKey> = {
	[SenderFailureContext.Upload]: "uploadFailed",
	[SenderFailureContext.Delivery]: "deliveryFailed",
	[SenderFailureContext.Email]: "emailFailed",
	[SenderFailureContext.Authentication]: "authenticationFailed",
};

/** Reads a standards-compliant delta-seconds Retry-After value from known delivery responses. */
export function readRetryAfterSeconds(error: unknown): number | null {
	const response = (error as {
		response?: {
			status?: number;
			headers?: Record<string, unknown> | {get(name: string): unknown};
		};
	} | null)?.response;
	if (response?.status !== 409 && response?.status !== 429) {
		return null;
	}
	const headers = response.headers;
	const raw = headers !== undefined && "get" in headers && typeof headers.get === "function"
		? headers.get("retry-after")
		: (headers as Record<string, unknown> | undefined)?.["retry-after"];
	if (typeof raw !== "string" || !/^\d+$/.test(raw.trim())) {
		return null;
	}
	return Number(raw.trim());
}

/**
 * The escapes that outrank a context's default key, each keyed by a value the ui reads — an error
 * class, the api's stable `errorId`, a transport verdict — never by matching translated prose
 * (kitecorp/transferify-ui#1664: the api localizes messages, so prose differs per locale).
 */
function specificFailureKey(error: unknown, base: RenderedFailureKey): SenderFailureKey | null {
	if (error instanceof EncryptedUploadTooLargeError) {
		return "fileTooLarge";
	}
	// The connection did not come back within the retry window (kitecorp/transferify-ui#3204): a network
	// failure, whatever request happened to be the last to give up.
	if (error instanceof UploadInterruptedError) {
		return "network";
	}
	// Account-level refusal: the plan's active-storage cap is already spent by LIVE transfers, so the
	// remedy is deleting one or upgrading — the context's "try again" copy would actively mislead.
	if (isActiveStorageRefusal(error)) {
		return "activeStorageFull";
	}
	// Per-transfer refusal: the selection holds more files than a transfer may. The ui's sentence names
	// the limit from the catalogue the api publishes it on, in the reader's own language, where the
	// api's German catalogue would answer in English.
	if (isTooManyFilesRefusal(error)) {
		return "tooManyFiles";
	}
	// Per-transfer refusal: no plan holds this size (kitecorp/transferify-api-service#3005). The ui's sentence names
	// the limit from the catalogue, for the same reason as the file limit above.
	if (isAboveLargestPlanRefusal(error)) {
		return "aboveLargestPlan";
	}
	// A FREE Instant send over the free size, which only a plan can take (kitecorp/transferify-api-service#3005, Q5).
	// CreateFlow answers it by opening the plan dialog; the key names the refusal, and the text below is what a caller
	// that shows it sees, in the reader's language and with the limit from the catalogue.
	if (isInstantPlanRequiredRefusal(error)) {
		return "instantPlanRequired";
	}
	// The same refusal on the hosted path, answered by the upload's registration (kitecorp/transferify-ui#3800).
	if (isFreeMaxUploadRefusal(error)) {
		return "hostedPlanRequired";
	}
	// The per-transfer personalization refusals (api#1099), reachable on the init AND on the completion
	// that attaches a draft composed during the upload. "Please try again" is wrong for every one of
	// them: two are contradictions the sender has to resolve, and two cannot succeed on a retry at all.
	const personalization = personalizationRefusalOf(error);
	if (personalization !== null) {
		return personalization;
	}
	return base === RenderedFailureKey.Network ? "network" : null;
}

/**
 * Classifies sender-facing failures. The only api prose that survives is the api's own sentence for a
 * refusal during the send ({@link SenderFailure.apiMessage}), and only where the ui has no more specific
 * key for it.
 *
 * "During the send" is the {@link SenderFailureContext.Upload} context, which `CreateFlow` gives to every
 * failure of a send that has not reached delivery: the upload init, which is the refusal this exists for,
 * and every multipart and completion request after it. A 400 from any of them is still the api refusing
 * and saying why, in the same envelope, so its sentence is shown too rather than a guess.
 *
 * WHY (kitecorp/transferify-ui#3010). A sender who had paid for an over-limit allowance was refused at
 * the init with a validation 400, most likely for more than the 1,000 files a transfer could then hold,
 * and was told "We couldn't send your files. Please try again." That sentence names nothing the sender
 * can change, so retrying and re-selecting the files were all that was left to try. The delivery, email
 * and sign-in contexts keep their own sentences: each of those already names the step to take next.
 */
export function classifySenderFailure(error: unknown, context: SenderFailureContext): SenderFailure {
	const base = classifyRenderedFailure(error, RenderedFailureKey.Generic);
	const specific = specificFailureKey(error, base.messageKey);
	return {
		messageKey: specific ?? KEY_BY_CONTEXT[context],
		retryAfterSeconds: context === SenderFailureContext.Delivery ? readRetryAfterSeconds(error) : null,
		apiMessage: specific === null && context === SenderFailureContext.Upload ? apiRefusalMessageOf(error) : null,
		diagnostic: base.diagnostic,
	};
}

/**
 * The sentence to show: the api's own sentence for a refusal during the send when it gave one, else the
 * key resolved through the active catalog, plus locale-owned retry guidance when present.
 */
export function senderFailureText(
	failure: SenderFailure,
	copy: Record<SenderFailureKey, string>,
	formatRetryAfter: (seconds: number) => string,
): string {
	const message = failure.apiMessage ?? copy[failure.messageKey];
	return failure.retryAfterSeconds === null
		? message
		: `${message} ${formatRetryAfter(failure.retryAfterSeconds)}`;
}

/**
 * The wall a plan refusal sends the sender back to, or null for every other failure. CreateFlow reads it to keep the
 * refusal and the composer to reopen that wall's dialog (kitecorp/transferify-ui#3800); the refused path decides the
 * wall, not the mode picked since.
 */
export function planRefusalPricing(key: SenderFailureKey): OverLimitPricing | null {
	switch (key) {
		case "instantPlanRequired":
			return "instant";
		case "hostedPlanRequired":
			return "hosted";
		default:
			return null;
	}
}

export function reportSenderFailure(context: SenderFailureContext, failure: SenderFailure): void {
	reportRenderedFailure(`sender_${context.toLowerCase()}`, failure);
}
